/**
 * CEO Command Center - Connection Status Store
 *
 * Tracks the health status of external API connections:
 * - OpenAI (AI features)
 * - News API (market intelligence)
 * - SQLite/Sync (local database)
 *
 * Note: Airtable has been removed as of v11. Data is stored locally in SQLite.
 *
 * @module stores/connectionStore
 * @version 11.0.0
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { API_CONFIG } from '../config/constants';

// ============================================
// TYPES
// ============================================

export type ConnectionStatus = 'unknown' | 'checking' | 'connected' | 'error';

export interface ConnectionInfo {
  status: ConnectionStatus;
  lastChecked: string | null;
  error: string | null;
  latency: number | null;
}

interface ConnectionState {
  // Connection statuses
  database: ConnectionInfo; // Local SQLite status
  openai: ConnectionInfo;
  newsApi: ConnectionInfo;

  // Overall status
  isOnline: boolean;

  // Actions
  checkDatabase: () => Promise<void>;
  checkOpenAI: () => Promise<void>;
  checkNewsApi: () => Promise<void>;
  checkAll: () => Promise<void>;
  setConnectionStatus: (
    service: 'database' | 'openai' | 'newsApi',
    status: Partial<ConnectionInfo>
  ) => void;
}

// ============================================
// INITIAL STATE
// ============================================

const initialConnectionInfo: ConnectionInfo = {
  status: 'unknown',
  lastChecked: null,
  error: null,
  latency: null,
};

// ============================================
// STORE
// ============================================

export const useConnectionStore = create<ConnectionState>()(
  devtools(
    (set, get) => ({
      // Initial state
      database: { ...initialConnectionInfo },
      openai: { ...initialConnectionInfo },
      newsApi: { ...initialConnectionInfo },
      isOnline: navigator.onLine,

      // Set connection status
      setConnectionStatus: (service, status) => {
        set((state) => ({
          [service]: { ...state[service], ...status },
        }));
      },

      // Check SQLite database status
      checkDatabase: async () => {
        const { setConnectionStatus } = get();
        setConnectionStatus('database', { status: 'checking', error: null });

        const startTime = Date.now();

        try {
          // Import sync store to check if database is initialized
          const { useSyncStore } = await import('./syncStore');
          const syncState = useSyncStore.getState();

          // If initialization is in progress, wait for it
          if (syncState.initializing) {
            // Wait up to 10 seconds for initialization
            let attempts = 0;
            while (syncState.initializing && attempts < 100) {
              await new Promise(resolve => setTimeout(resolve, 100));
              attempts++;
            }
          }

          // Re-check state after waiting
          const currentState = useSyncStore.getState();
          const latency = Date.now() - startTime;

          if (currentState.initialized) {
            setConnectionStatus('database', {
              status: 'connected',
              lastChecked: new Date().toISOString(),
              error: null,
              latency,
            });
          } else if (currentState.initError) {
            setConnectionStatus('database', {
              status: 'error',
              lastChecked: new Date().toISOString(),
              error: currentState.initError,
              latency,
            });
          } else {
            setConnectionStatus('database', {
              status: 'error',
              lastChecked: new Date().toISOString(),
              error: 'Database not initialized. Click "Set Up Local Storage" to get started.',
              latency,
            });
          }
        } catch (error) {
          const latency = Date.now() - startTime;
          setConnectionStatus('database', {
            status: 'error',
            lastChecked: new Date().toISOString(),
            error: error instanceof Error ? error.message : 'Failed to check database',
            latency,
          });
        }
      },

      // Check OpenAI connection
      checkOpenAI: async () => {
        const { setConnectionStatus } = get();
        setConnectionStatus('openai', { status: 'checking', error: null });

        const startTime = Date.now();

        try {
          // Check via API health endpoint
          const response = await fetch(`${API_CONFIG.BASE_URL}/api/health`, {
            method: 'GET',
          });

          const latency = Date.now() - startTime;

          if (response.ok) {
            const data = await response.json();
            const hasOpenAI = data.services?.openai !== false;

            setConnectionStatus('openai', {
              status: hasOpenAI ? 'connected' : 'error',
              lastChecked: new Date().toISOString(),
              error: hasOpenAI ? null : 'OpenAI API key not configured',
              latency,
            });
          } else {
            setConnectionStatus('openai', {
              status: 'error',
              lastChecked: new Date().toISOString(),
              error: 'Could not verify OpenAI status',
              latency,
            });
          }
        } catch (error) {
          setConnectionStatus('openai', {
            status: 'error',
            lastChecked: new Date().toISOString(),
            error: 'API service not available',
            latency: null,
          });
        }
      },

      // Check News API connection
      checkNewsApi: async () => {
        const { setConnectionStatus } = get();

        setConnectionStatus('newsApi', { status: 'checking', error: null });

        const startTime = Date.now();

        try {
          const response = await fetch(`${API_CONFIG.BASE_URL}/api/health`, {
            method: 'GET',
          });

          const latency = Date.now() - startTime;

          if (response.ok) {
            const data = await response.json();
            const hasNewsApi = data.services?.news !== false;

            setConnectionStatus('newsApi', {
              status: hasNewsApi ? 'connected' : 'unknown',
              lastChecked: new Date().toISOString(),
              error: hasNewsApi ? null : 'News API not configured (optional)',
              latency,
            });
          } else {
            setConnectionStatus('newsApi', {
              status: 'unknown',
              lastChecked: new Date().toISOString(),
              error: 'Could not verify News API status',
              latency,
            });
          }
        } catch (error) {
          setConnectionStatus('newsApi', {
            status: 'unknown',
            lastChecked: new Date().toISOString(),
            error: 'Connection check failed',
            latency: null,
          });
        }
      },

      // Check all connections
      checkAll: async () => {
        const { checkDatabase, checkOpenAI, checkNewsApi } = get();

        // Check all in parallel
        await Promise.all([checkDatabase(), checkOpenAI(), checkNewsApi()]);
      },
    }),
    { name: 'ceo-connection-store' }
  )
);

// ============================================
// HOOKS
// ============================================

/**
 * Get overall connection health
 */
export const useConnectionHealth = () => {
  return useConnectionStore((state) => ({
    isHealthy: state.database.status === 'connected',
    database: state.database.status,
    openai: state.openai.status,
    newsApi: state.newsApi.status,
  }));
};

/**
 * Get connection error message if any
 */
export const useConnectionError = () => {
  return useConnectionStore((state) => {
    if (state.database.status === 'error') {
      return state.database.error;
    }
    return null;
  });
};
